import { doc, collection, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { canEditStudent } from '@/config/roles'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Role-aware student lifecycle. The smoke student is created OWNED by the
// current user (caseManagerId; staffIds is populated by the server), so reads/edits exercise the
// "own student" path — each step asserts the server's answer against the rules
// mirror / client canEditStudent, and a correct denial counts as a pass.
//
// The create fires updateStudentStaffIds like any real write (dev project).
export default {
  id: 'studentsCrud',
  label: 'Student create/read/update/delete',
  description: 'Owned-student lifecycle; every step asserted against the rules for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const ref = doc(collection(db, 'students'))

    const studentDoc = {
      app: {
        studentData: {
          firstName: 'SMOKE',
          lastName: `Test ${ctx.runId.slice(-8)}`,
          plan: 'IEP',
          grade: '9',
          ssid: `SMOKE-${ctx.runId}`,
          caseManagerId: ctx.user.uid
        },
        accommodations: { instruction: '', assessment: '' },
        schedule: { classServices: [] },
        staffIds: []
      },
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
    }

    const expectCreate = RULE_EXPECTATIONS.studentCreate(role)
    const expectDelete = RULE_EXPECTATIONS.studentDelete(role)
    // Client contract for editing your OWN IEP student — the server must agree.
    const expectEdit = canEditStudent(
      {
        caseManagerId: ctx.user.uid,
        app: { studentData: { caseManagerId: ctx.user.uid, plan: 'IEP' } }
      },
      { uid: ctx.user.uid, role }
    )

    let created = false
    try {
      await step(`create student (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() => setDoc(ref, studentDoc))
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — students/${ref.id}`
          : accessDetail(expectCreate, allowed)
      })

      // Nothing to read/update/delete if the role can't create (denial already
      // asserted above as a pass).
      if (!created) {
        step.skip('remaining lifecycle', 'Role cannot create — later steps skipped')
        return
      }

      await step('read student back (expected: allowed — you own it)', async () => {
        const { allowed } = await attempt(async () => {
          const snap = await getDoc(ref)
          if (!snap.exists()) throw new Error('Created doc not found on read-back')
          if (snap.data()?.app?.studentData?.ssid !== studentDoc.app.studentData.ssid) {
            throw new Error('Read-back data does not match what was written')
          }
        })
        return accessDetail(true, allowed)
      })

      await step(`update student (client canEdit: ${expectEdit})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(ref, { app: { studentData: { grade: '10' } } }, { merge: true })
        )
        return accessDetail(expectEdit, allowed)
      })

      await step(`delete student (expected: ${fmtExpected(expectDelete)})`, async () => {
        const { allowed } = await attempt(() => deleteDoc(ref))
        return accessDetail(expectDelete, allowed)
      })
    } finally {
      // Super-admin-tier delete only; leftovers for lower roles are swept by
      // the panel's "Clean up leftovers" button.
      await deleteDoc(ref).catch(() => {})
    }
  }
}
